import React, { useState, useEffect, useRef, useMemo } from "react";
import { PatientSearch } from "../SearchPatients/SearchPatients";
import {
  fetchUserServices,
  getUserPracticeById,
  getAppointmentsByPracticeIds,
  createAppointment,
  calculateAppointment,
  checkRecurringSchedule,
  createRecurringAppointments,
} from "../../api";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Alert from "react-bootstrap/Alert";
import AppToast from "../Toasts/AppToast";
import { useToast } from "../Toasts/useToast";
import { createIntakeSession, getIntakeEnabledForPractice } from "../../intakeApi";
import ServiceLinePicker from "../Services/ServiceLinePicker";
import { getUserPreferences } from "../BootNav/userPreferences";
import "./FullCalendar.css";

export function AddAppointmentModal({
  show,
  onHide,
  selectedDate,
  resourcesId,
  resourcesName,
  addNotification,
  setEvents,
  practiceIds,
}) {
  const [patient, setPatient] = useState({});
  const { toast, showToast, hideToast } = useToast();
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState(null);
  const [servicesDur, setServicesDur] = useState();
  const [selectedServicesDescr, setSelectedServicesDescr] = useState();
  const [practices, setPractices] = useState([]);
  const [selectedPracticeId, setSelectedPracticeId] = useState([]);
  const [selectedServicesId, setSelectedServicesId] = useState([]);
  const [newPatient, setNewPatient] = useState(false);
  const [sendIntakeEmail, setSendIntakeEmail] = useState(false);
  const [sendIntakeSMS, setSendIntakeSMS] = useState(false);
  const [intakeEnabled, setIntakeEnabled] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const modalBodyRef = useRef(null);
  const [alertMessage, setAlertMessage] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [suggestedStart, setSuggestedStart] = useState(null); // Holds the accepted suggestion (or first suggestion)
  const [alertSuggestions, setAlertSuggestions] = useState([]); // Holds the three suggestions from API
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [errors, setErrors] = useState({});
  const [recurring, setRecurring] = useState(false);
  const [lastChangedField, setLastChangedField] = useState("numberOfSessions");
  const [recurringMessage, setRecurringMessage] = useState(false);
  const [conflictsAlert, setConflictsAlert] = useState(false);
  const [availableAlert, setAvailableAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [locationsForPractice, setLocationsForPractice] = useState([]);
  const [selectedLocationIdx, setSelectedLocationIdx] = useState(0);

  function formatPhoneIntlZA(raw) {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");

    // already +27XXXXXXXXX
    if (/^\+27\d{9}$/.test(raw)) return raw;

    // 27XXXXXXXXX → +27XXXXXXXXX
    if (digits.startsWith("27") && digits.length === 11) return `+${digits}`;

    // 0XXXXXXXXX → +27XXXXXXXXX
    if (digits.startsWith("0") && digits.length === 10)
      return `+27${digits.slice(1)}`;

    // 9-digit local (missing leading 0) → +27XXXXXXXXX
    if (digits.length === 9) return `+27${digits}`;

    // fallback: if looks numeric, prefix +
    if (digits.length > 0 && !raw.startsWith("+")) return `+${digits}`;

    return raw.replace(/\s+/g, "");
  }

  function formatDateToInputValue(dateString) {
    const date = new Date(Date.parse(dateString)); // safer parsing
    if (isNaN(date)) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }
  const dateObj = new Date(selectedDate);

  // Build a locations array from a practice (backward compatible)
  function deriveLocationsFromPractice(practice) {
    if (!practice) return [];

    // If practice already has a non-empty locations array, use it
    if (Array.isArray(practice.locations) && practice.locations.length > 0) {
      return practice.locations;
    }

    // Otherwise synthesize from legacy address fields
    return [
      {
        name: practice.primaryLocationName || "Main Location",
        address1: practice.practiceAddress || "",
        address2: practice.practiceAddress2 || "",
        city: practice.practiceCity || "",
        province: practice.practiceProvince || "",
        postalCode: practice.practicePostalCode || "",
      },
    ];
  }

  const initialRecurringData = {
    recurringStartTime: dateObj.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
    recurringEndTime: "",
    numberOfSessions: 1,
    recurringStartDate: dateObj.toISOString().split("T")[0],
    recurringEndDate: "",
    recurringDays: [],
  };

  const initialAppointment = {
    title: "",
    patientFirstName: "",
    patientLastName: "",
    start: selectedDate || "",
    end: "",
    patientId: "",
    serviceId: "",
    reminderInterval: [],
    reminderType: [],
    notes: "",
    resourceName: resourcesName || "",
    resourceId: resourcesId || "",
    practiceId: "",
    status: "Pending",
    eventColor: "",
    patientEmail: "",
    patientPhone: "",
    borderColor: "",
    backgroundColor: "",
    appointmentCreatedBy: "",
    appointmentCreatedDate: "",
    appointmentConfirmationEmail: !!getUserPreferences().autoConfirmAppointments,
    recurringData: "",
    appointmentLocation: {},
  };
  const [appointment, setAppointment] = useState(initialAppointment);
  const [recurringData, setRecurringData] = useState(initialRecurringData);
  // Once the user hand-edits the End field, suppress the auto-recalc on
  // start/service change so end doesn't snap back to start + duration.
  // Cleared on modal close.
  const endManuallyEditedRef = useRef(false);

  useEffect(() => {
    if (!show || !selectedDate || !resourcesId) return;

    setAppointment((prevState) => ({
      ...prevState,
      start: formatDateToInputValue(selectedDate),
      resourceId: resourcesId,
      resourceName: resourcesName,
    }));

    setRecurringData((prevState) => ({
      ...prevState,
      recurringStartTime:
        dateObj.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }) || "",
      recurringStartDate: dateObj.toISOString().split("T")[0] || "",
    }));

    // Fetch services and practices in parallel
    const loadData = async () => {
      setServicesLoading(true);
      setServicesError(null);

      try {
        const [servicesResponse, practicesResponse] = await Promise.all([
          fetchUserServices(resourcesId),
          getUserPracticeById(resourcesId),
        ]);

        // Set services
        if (Array.isArray(servicesResponse) && servicesResponse.length > 0) {
          setServices(servicesResponse);
        } else {
          setServicesError("No services available for this practitioner");
          setServices([]);
        }

        // Set practices
        if (Array.isArray(practicesResponse) && practicesResponse.length > 0) {
          setPractices(practicesResponse);
          const firstPractice = practicesResponse[0];
          const locs = deriveLocationsFromPractice(firstPractice);

          setSelectedPracticeId(firstPractice.practiceNumber);
          setLocationsForPractice(locs);
          setSelectedLocationIdx(0);

          setAppointment((prev) => ({
            ...prev,
            practiceId: firstPractice.practiceNumber,
            practiceName: firstPractice.practiceName || "",
            location: { ...locs[0] },
            locationName: locs[0].name || "Location",
            locationAddress: [
              locs[0].address1,
              locs[0].address2,
              [locs[0].city, locs[0].province].filter(Boolean).join(", "),
              locs[0].postalCode,
            ]
              .filter(Boolean)
              .join(" | "),
          }));
        }
      } catch (error) {
        console.error("Error loading appointment data:", error);
        setServicesError("Failed to load services. Please try again.");
        setServices([]);
      } finally {
        setServicesLoading(false);
      }
    };

    loadData();
  }, [show, selectedDate, resourcesId, resourcesName]);

  useEffect(() => {
    setAppointment((prevState) => ({
      ...prevState,
      notes: selectedServicesDescr,
    }));
  }, [appointment.serviceId, selectedServicesDescr]);

  useEffect(() => {
    if (appointment.practiceId !== "" && !newPatient) {
      setAppointment((prevState) => ({
        ...prevState,
        status: "Confirmed",
      }));
    }
  }, [appointment.practiceId, newPatient]);

  // Check if intake is enabled for the selected practice (T&C configured)
  useEffect(() => {
    if (!appointment.practiceId) { setIntakeEnabled(false); return; }
    getIntakeEnabledForPractice(appointment.practiceId).then(setIntakeEnabled);
  }, [appointment.practiceId]);

  useEffect(() => {
    if (newPatient) {
      const updatedTitle = `${appointment.patientFirstName || ""} ${
        appointment.patientLastName || ""
      }`.trim();
      setAppointment((prevState) => ({
        ...prevState,
        title: updatedTitle,
      }));
    } else if (patient && Object.keys(patient).length > 0) {
      const updatedTitle = `${patient.patientTitle || ""} ${
        patient.patientFirstName || ""
      } ${patient.patientLastName || ""}`.trim();
      setAppointment((prevState) => ({
        ...prevState,
        title: updatedTitle,
        patientId: patient._id,
      }));
    }
  }, [
    patient,
    newPatient,
    appointment.patientFirstName,
    appointment.patientLastName,
  ]);

  // Filter practices to only those the selected patient is linked to
  const filteredPractices = useMemo(() => {
    const linked = patient?.patientPractice;
    if (!Array.isArray(linked) || linked.length === 0) return practices;
    const linkedSet = new Set(linked.map(String));
    return practices.filter((p) => linkedSet.has(String(p.practiceNumber)));
  }, [practices, patient]);

  // When the filtered practice list changes, reset to the first valid practice if current is no longer in the list
  useEffect(() => {
    if (!filteredPractices.length) return;
    const stillValid = filteredPractices.some(
      (p) => String(p.practiceNumber) === String(selectedPracticeId)
    );
    if (!stillValid) {
      const first = filteredPractices[0];
      const locs = deriveLocationsFromPractice(first);
      setSelectedPracticeId(first.practiceNumber);
      setLocationsForPractice(locs);
      setSelectedLocationIdx(0);
      setAppointment((prev) => ({
        ...prev,
        practiceId: first.practiceNumber,
        practiceName: first.practiceName || "",
        location: { ...locs[0] },
        locationName: locs[0]?.name || "Location",
        locationAddress: [
          locs[0]?.address1,
          locs[0]?.address2,
          [locs[0]?.city, locs[0]?.province].filter(Boolean).join(", "),
          locs[0]?.postalCode,
        ]
          .filter(Boolean)
          .join(" | "),
      }));
    }
  }, [filteredPractices]);

  useEffect(() => {
    if (!recurring || !recurringData.recurringStartDate) return;

    const {
      recurringDays,
      recurringStartDate,
      recurringEndDate,
      numberOfSessions,
    } = recurringData;

    const dayMap = {
      Sunday: 0,
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
    };
    const recurringDayNums = recurringDays.map((day) => dayMap[day]);
    if (recurringDayNums.length === 0) return;

    const start = new Date(recurringStartDate);

    if (lastChangedField === "recurringEndDate" && recurringEndDate) {
      const end = new Date(recurringEndDate);
      let sessionCount = 0;
      for (
        let date = new Date(start);
        date <= end;
        date.setDate(date.getDate() + 1)
      ) {
        if (recurringDayNums.includes(date.getDay())) sessionCount++;
      }

      setRecurringData((prev) => ({
        ...prev,
        numberOfSessions: sessionCount,
      }));
    }

    if (lastChangedField === "numberOfSessions" && numberOfSessions > 0) {
      let sessionsRemaining = numberOfSessions;
      let currentDate = new Date(start);

      while (sessionsRemaining > 0) {
        if (recurringDayNums.includes(currentDate.getDay())) {
          sessionsRemaining--;
        }
        if (sessionsRemaining > 0) {
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }

      setRecurringData((prev) => ({
        ...prev,
        recurringEndDate: currentDate.toISOString().split("T")[0],
      }));
    }
  }, [
    recurringData.numberOfSessions,
    recurringData.recurringEndDate,
    recurringData.recurringDays,
    recurringData.recurringStartDate,
    recurring,
    lastChangedField,
  ]);

  const handleChangeAppointment = async (e) => {
    const { type, name, value, checked } = e.target;

    // Always update the appointment state first
    setAppointment((prevState) => ({
      ...prevState,
      [name]: name === "newPatient" ? checked : value,
    }));

    // Handle "newPatient" logic
    if (name === "newPatient") {
      setNewPatient(checked);
      setAppointment((prevState) => ({
        ...prevState,
        patientId: checked ? "" : patient._id,
        status: checked ? "New Patient" : "Confirmed",
      }));
      return;
    }

    // Handle "scheduling" toggle
    if (name === "scheduling") {
      setScheduling(checked);
      setAppointment((prevState) => ({
        ...prevState,
        appointmentConfirmationEmail: !checked,
        status: checked
          ? "Scheduling"
          : (newPatient ? "New Patient" : "Confirmed"),
        borderColor: checked ? "#D3D3D3" : "",
        backgroundColor: checked ? "#D3D3D3" : "",
        title: checked ? "Unavailable" : "",
      }));

      return;
    }

    const RECUR_FIELDS = new Set([
      "recurringStartTime",
      "recurringEndTime",
      "numberOfSessions",
      "recurringStartDate",
      "recurringEndDate",
      "recurringDays",
    ]);
    if (RECUR_FIELDS.has(name)) {
      setRecurringData((prev) => ({ ...prev, [name]: value }));
    }

    if (name === "numberOfSessions" || name === "recurringEndDate") {
      setLastChangedField(name);
    }

    if (
      name === "recurringStartTime" &&
      recurring && // only in recurring mode
      servicesDur && // must have a duration from your selected service
      appointment.start // need the base date
    ) {
      const [durH, durM] = servicesDur.split(":").map(Number);
      const [startH, startM] = value.split(":").map(Number);

      // build a Date on the appointment.start day
      const base = new Date(appointment.start);
      base.setHours(startH, startM, 0, 0);

      // add the service duration in minutes
      const end = new Date(base.getTime() + (durH * 60 + durM) * 60000);

      const formattedEnd = end.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      // push it back into your recurringData
      setRecurringData((prev) => ({
        ...prev,
        recurringStartTime: value,
        recurringEndTime: formattedEnd,
      }));
    }

    // ✅ When "start" changes — recalculate appointment if service is selected,
    // but only if the user hasn't hand-edited end. Once end is user-owned we
    // leave it alone on start changes.
    if (
      name === "start" &&
      appointment.serviceId &&
      !endManuallyEditedRef.current
    ) {
      const selectedService = services.find(
        (s) => s._id === appointment.serviceId
      );

      if (selectedService) {
        const appointmentData = {
          appointment: {
            start: value,
            resourceId: appointment.resourceId,
            practitionerName: resourcesName || "",
          },
          selectedService: {
            servicesDuration: selectedService.servicesDuration,
            serviceId: selectedService._id,
          },
        };

        try {
          const result = await calculateAppointment(appointmentData);

          if (result.endTime) {
            setAppointment((prev) => ({
              ...prev,
              end: formatDateToInputValue(new Date(result.endTime)),
            }));
            setAlertMessage(false);
            setAlertSuggestions([]);
          } else if (result.suggestions) {
            setAppointment((prev) => ({
              ...prev,
              end: "",
            }));
            setAlertMessage(true);
            setAlertText(
              "The selected time is unavailable. Please choose one of the suggested start times below."
            );
            setAlertSuggestions(result.suggestions);
            setSuggestedStart(result.suggestions[0].suggestedStartTime);
          }
        } catch (error) {
          console.error(
            "❌ Error recalculating appointment on start change:",
            error
          );
        }
      }
    }
  };

  const handleServiceChange = async (e) => {
    const selectedServiceId = e.target.value;
    const selectedService = services.find(
      (service) => service._id === selectedServiceId
    );

    if (!selectedService) return;

    setServicesDur(selectedService.servicesDuration);
    setSelectedServicesId(selectedService._id);
    setSelectedServicesDescr(selectedService.servicesDescription);

    const appointmentData = {
      appointment: {
        start: appointment.start || selectedDate,
        resourceId: appointment.resourceId,
      },
      selectedService: {
        servicesDuration: selectedService.servicesDuration,
        serviceId: selectedService._id,
      },
    };

    try {
      if (!recurring) {
        const result = await calculateAppointment(appointmentData);

        if (result.endTime) {
          setAppointment((prevState) => ({
            ...prevState,
            serviceId: selectedServiceId,
            // Only adopt the duration-derived end when the user hasn't already
            // overridden it. Keeps a hand-edited end intact when the service
            // is changed afterwards.
            end: endManuallyEditedRef.current
              ? prevState.end
              : formatDateToInputValue(new Date(result.endTime)),
            patientId: patient._id,
          }));

          if (result.warnings?.length) {
            const messages = [];

            if (result.warnings.includes("HARD_CONFLICT")) {
              messages.push(
                "This appointment overlaps with another booking. It will be scheduled — confirm this is intentional before saving."
              );
            }

            if (result.warnings.includes("AVAILABILITY_CONFLICT")) {
              messages.push(
                "The appointment overlaps with staff unavailability. It will be scheduled, please ensure availability."
              );
            }

            if (result.warnings.includes("OUTSIDE_BUSINESS_HOURS")) {
              messages.push(
                "The appointment falls outside of standard business hours. It will be scheduled, please ensure availability."
              );
            }

            setAlertMessage(true);
            setAlertText(messages); // 👈 Pass as array
          } else {
            setAlertMessage(false);
            setAlertText([]);
          }
        }
      } else {
        setAppointment((prevState) => ({
          ...prevState,
          serviceId: selectedServiceId,
        }));

        setRecurringData((prev) => ({
          ...prev,
          recurringEndTime:
            selectedService.servicesDuration && recurringData.recurringStartTime
              ? (() => {
                  const [durH, durM] = selectedService.servicesDuration
                    .split(":")
                    .map(Number);
                  const [startH, startM] = recurringData.recurringStartTime
                    .split(":")
                    .map(Number);
                  const base = new Date(appointment.start);
                  base.setHours(startH, startM, 0, 0);
                  const end = new Date(
                    base.getTime() + (durH * 60 + durM) * 60000
                  );
                  return end.toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  });
                })()
              : "",
        }));
      }
    } catch (error) {
      console.error("Error calculating appointment end time:", error);
    }
  };

  const checkSchedule = async () => {
    if (recurring) {
      try {
        const scheduleData = {
          appointment: {
            start: recurringData.recurringStartDate,
            resourceId: appointment.resourceId,
          },
          selectedService: {
            servicesDuration: servicesDur,
            serviceId: selectedServicesId,
          },
          recurringData: recurringData,
        };

        const result = await checkRecurringSchedule(scheduleData);
        setRecurringMessage(true);
        setConflictsAlert(result.conflicts);
        setAvailableAlert(result.available);

        console.log("Check Schedule Result:", result);
      } catch (error) {
        console.error("Error checking schedule:", error);
      }
    }
  };

  function validateForm() {
    let newErrors = {};

    if (newPatient) {
      if (
        !appointment.patientEmail ||
        !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(appointment.patientEmail)
      ) {
        newErrors.patientEmail = "Enter a valid email address.";
      }
      if (
        appointment.patientPhone &&
        !/^\+27\d{9}$/.test(appointment.patientPhone)
      ) {
        newErrors.patientPhone = "Enter a valid SA phone, e.g. +27821234567.";
      }
      if (!appointment.patientFirstName)
        newErrors.patientFirstName = "First name is required.";
      if (!appointment.patientLastName)
        newErrors.patientLastName = "Last name is required.";
    }

    if (!scheduling) {
      if (!appointment.serviceId)
        newErrors.serviceId = "Service selection is required.";
      if (!appointment.practiceId)
        newErrors.practiceId = "Practice selection is required.";
    }

    // Both start and end are required so we never persist `end: ""`, which
    // later renders as 1970-01-01T02:00 in the modal (epoch in SAST). The
    // recurring path manages its own date range and skips the single-end
    // picker, so it doesn't need this guard.
    if (!recurring) {
      if (!appointment.start) {
        newErrors.start = "Start time is required.";
      }
      if (!appointment.end) {
        newErrors.end = "End time is required.";
      }
    }

    if (appointment.start && appointment.end) {
      const s = new Date(appointment.start).getTime();
      const e = new Date(appointment.end).getTime();
      if (!isNaN(s) && !isNaN(e) && s >= e) {
        newErrors.end = "End time must be after the start time.";
      }
    }

    if (!newPatient && !scheduling && !appointment.patientId) {
      newErrors.patientId = "A patient must be selected.";
    }

    // Title is always required
    if (!appointment.title || !appointment.title.trim()) {
      newErrors.title = "Title is required.";
    }

    setErrors(newErrors);

    console.log("Validation Errors:", newErrors); // 🔹 Debugging step

    return Object.keys(newErrors).length === 0; // Return false if errors exist
  }

  async function handleSaveAppointment() {
    if (!validateForm()) {
      console.error("Validation failed!");
      return;
    }

    // Ensure a valid location is set (backward compatible)
    let finalAppt = { ...appointment };
    if (!finalAppt.location) {
      const p = practices.find(
        (x) => x.practiceNumber === finalAppt.practiceId
      );
      const locs = deriveLocationsFromPractice(p);
      const loc = locs[0] || {};
      finalAppt.location = { ...loc };
      finalAppt.locationName = loc.name || "Location";
      finalAppt.locationAddress = [
        loc.address1,
        loc.address2,
        [loc.city, loc.province].filter(Boolean).join(", "),
        loc.postalCode,
      ]
        .filter(Boolean)
        .join(" | ");
    }

    // If either intake checkbox is checked, create the session now and attach the tokenId
    if ((sendIntakeEmail && appointment.patientEmail) || (sendIntakeSMS && appointment.patientPhone)) {
      try {
        const intakeResult = await createIntakeSession({
          practiceNumber: selectedPracticeId || appointment.practiceId,
          patientName: [appointment.patientFirstName, appointment.patientLastName].filter(Boolean).join(" "),
          deliveryEmail: sendIntakeEmail ? appointment.patientEmail : null,
          deliverySMS: sendIntakeSMS ? appointment.patientPhone : null,
        });
        if (intakeResult.tokenId) {
          finalAppt = { ...finalAppt, intakeSessionTokenId: intakeResult.tokenId };
        }
      } catch {
        // Non-fatal — appointment still saves without intake link
      }
    }

    setScheduling(true);
    setIsLoading(true);
    try {
      let savedAppointment;

      if (!recurring) {
        const res = await createAppointment(finalAppt);
        savedAppointment = res.data?.appointment;
      } else {
        const data = {
          appointment: {
            ...finalAppt,
            start: recurringData.recurringStartDate,
            recurringData,
          },
          selectedService: {
            servicesDuration: servicesDur,
            serviceId: selectedServicesId,
          },
          recurringData: recurringData,
        };
        const res = await createRecurringAppointments(data);
        savedAppointment = res.appointment;
      }

      // ✅ MOVE THIS OUTSIDE - runs for BOTH appointment types
      const updatedAppointments = await getAppointmentsByPracticeIds(
        practiceIds
      );

      const formattedEvents = updatedAppointments.map((evt) => ({
        ...evt,
        extendedProps: {
          ...evt,
          patientEmail: evt.patientEmail,
          patientPhone: evt.patientPhone,
        },
      }));

      setEvents(formattedEvents);

      // ✅ Close modal and show success toast
      showToast("success", "The appointment was successfully added!");
      onHide();

      // ✅ Notify that confirmation email will be sent
      if (
        appointment.appointmentConfirmationEmail &&
        appointment.patientEmail
      ) {
        addNotification({
          message: `Confirmation email scheduled to ${appointment.patientEmail}`,
          timestamp: Date.now(),
          type: "email",
        });
      }
    } catch (error) {
      console.error("Error saving appointment:", error);
    } finally {
      setScheduling(false);
      setSelectedServicesDescr("");
      setSelectedServicesId("");
      setSelectedPracticeId("");
      setNewPatient(false);
      setIsLoading(false);
      setServicesDur("");
      setPatient({});
      setAppointment(initialAppointment);
      setRecurringData(initialRecurringData);
      setRecurring(false);
      setRecurringMessage(false);
      setSendIntakeEmail(false);
      setSendIntakeSMS(false);
      // Don't clear services - they'll be refetched when modal opens
    }
  }

  function handleClose() {
    setAppointment(initialAppointment);
    setRecurringData(initialRecurringData);
    setSelectedServicesDescr("");
    setSelectedPracticeId("");
    setNewPatient(false);
    setSelectedServicesId("");
    setServicesDur("");
    setPatient({});
    setAlertText("");
    setSelectedSuggestion(null);
    setAlertMessage(false);
    setScheduling(false);
    setRecurring(false);
    setRecurringMessage(false);
    endManuallyEditedRef.current = false;
    onHide();
  }

  const handleEndTimeChange = (e) => {
    setAppointment((prevState) => ({
      ...prevState,
      end: e.target.value,
    }));
    // Mark end as user-overridden so subsequent start/service changes
    // don't snap it back to start + duration.
    endManuallyEditedRef.current = true;
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        centered
        dialogClassName="responsive-modal"
      >
        <Modal.Header closeButton>
          <div className="w-100">
            <Modal.Title className="mb-1">Add Appointment</Modal.Title>
            {appointment.status && (
              <span
                className={`d-block fw-bold ${
                  appointment.status === "Pending" ? "text-muted" : ""
                } ${appointment.status === "Confirmed" ? "text-success" : ""} ${
                  appointment.status === "New Patient" ? "text-danger" : ""
                } ${appointment.status === "Scheduling" ? "text-primary" : ""}`}
              >
                {appointment.status}
              </span>
            )}
          </div>
        </Modal.Header>
        {alertMessage && (
          <Alert variant="info" className="w-100">
            {/* Alert Text: supports multiple lines */}
            {alertText && (
              <div className="mb-0">
                <p className="mb-1 fw-bold text-primary">Please Note:</p>
                {Array.isArray(alertText) ? (
                  <ul className="mb-0 ps-3 text-primary">
                    {alertText.map((text, idx) => (
                      <li key={idx}>{text}</li>
                    ))}
                  </ul>
                ) : (
                  <strong>{alertText}</strong>
                )}
              </div>
            )}
          </Alert>
        )}
        {recurringMessage && (
          <Alert variant="info" className="w-100">
            <div className="mb-0">
              {conflictsAlert && conflictsAlert.length > 0 && (
                <>
                  <p className="mb-1 fw-bold text-primary">
                    The following dates have conflicts. For these you should go
                    to the date and create an appointment that fits the
                    schedule.
                  </p>
                  {Array.isArray(conflictsAlert) ? (
                    <ul className="mb-0 ps-3 text-primary">
                      {conflictsAlert.map((conflict, idx) => (
                        <li key={idx}>
                          {conflict.date}: {conflict.startTime} -{" "}
                          {conflict.endTime}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <strong>{conflictsAlert}</strong>
                  )}
                </>
              )}

              <p className="mb-1 fw-bold text-primary">
                The following dates will be scheduled after saving the
                appointment.
              </p>
              {Array.isArray(availableAlert) ? (
                <ul className="mb-0 ps-3 text-primary">
                  {availableAlert.map((available, idx) => (
                    <li key={idx}>
                      {available.date}: {available.startTime} -{" "}
                      {available.endTime}
                    </li>
                  ))}
                </ul>
              ) : (
                <strong>{availableAlert}</strong>
              )}
            </div>
          </Alert>
        )}

        <Modal.Body
          ref={modalBodyRef}
          style={{ overflowY: "auto", maxHeight: "600px" }}
        >
          <Form>
            <Row>
              <Form.Group as={Col} controlId="checkboxGroup">
                <Form.Check
                  type="switch"
                  label="Staff Scheduling"
                  name="scheduling"
                  checked={scheduling}
                  onChange={handleChangeAppointment}
                />
              </Form.Group>
            </Row>
            {!scheduling && (
              <>
                {/* Recurring switch */}
                <Col xs="auto" className="d-flex align-items-center">
                  <Form.Group controlId="checkboxGroup">
                    <Form.Check
                      className="mb-0"
                      type="switch"
                      label="Recurring"
                      name="recurring"
                      checked={recurring}
                      disabled={newPatient}
                      onChange={(e) => {
                        setRecurring(e.target.checked);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Row className="mb-3">
                  <Form.Group as={Col} controlId="checkboxGroup">
                    <Form.Check
                      type="switch"
                      label="New Patient"
                      name="newPatient"
                      checked={newPatient}
                      disabled={recurring}
                      onChange={handleChangeAppointment}
                    />
                  </Form.Group>
                </Row>
                {!newPatient && (
                  <Row className="mb-3">
                    <PatientSearch
                      onPatientSelect={(patient) => {
                        setPatient(patient);
                        console.log("selected patient:", patient);
                        setAppointment((prevState) => ({
                          ...prevState,
                          patientId: patient._id,
                          patientFirstName: patient.patientFirstName,
                          patientLastName: patient.patientLastName,
                          patientPhone: formatPhoneIntlZA(
                            patient.patientCellNumber
                          ),
                          patientEmail: patient.patientEmail,
                        }));
                      }}
                    />
                    {errors.patientId && (
                      <span className="text-danger">{errors.patientId}</span>
                    )}
                  </Row>
                )}
                {newPatient && (
                  <>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group controlId="patientEmail">
                          <Form.Label>Patient Email</Form.Label>
                          <Form.Control
                            name="patientEmail"
                            value={appointment.patientEmail || ""}
                            onChange={handleChangeAppointment}
                            isInvalid={!!errors.patientEmail}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.patientEmail}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="patientPhone">
                          <Form.Label>Patient Phone (optional)</Form.Label>
                          <Form.Control
                            type="tel"
                            name="patientPhone"
                            placeholder="+27821234567"
                            value={appointment.patientPhone || ""}
                            onChange={(e) => {
                              // keep raw while typing (prevents 2727 loops)
                              const v = e.target.value;
                              setAppointment((prev) => ({
                                ...prev,
                                patientPhone: v,
                              }));
                            }}
                            onBlur={(e) => {
                              // normalize ONLY when user leaves the field
                              const normalized = formatPhoneIntlZA(
                                e.target.value
                              );
                              setAppointment((prev) => ({
                                ...prev,
                                patientPhone: normalized,
                              }));
                            }}
                            inputMode="tel"
                            isInvalid={!!errors.patientPhone}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.patientPhone}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}
                <Row className="mb-3">
                  <Form.Group controlId="patientFirstName">
                    <Form.Label>Patient First Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="patientFirstName"
                      value={appointment.patientFirstName}
                      onChange={handleChangeAppointment}
                      isInvalid={!!errors.patientFirstName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.patientFirstName}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group controlId="patientLastName">
                    <Form.Label>Patient Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="patientLastName"
                      value={appointment.patientLastName}
                      onChange={handleChangeAppointment}
                      isInvalid={!!errors.patientLastName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.patientLastName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
              </>
            )}
            <hr />

            <Row className="mb-3">
              <Col>
                <Form.Group controlId="appointmentTitle">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    placeholder="For new patients enter their First Name and Last Name"
                    value={appointment.title}
                    onChange={handleChangeAppointment}
                    isInvalid={!!errors.title}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.title}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group controlId="appointmentResourceId">
                  <Form.Label>Therapist</Form.Label>
                  <Form.Control
                    readOnly
                    type="text"
                    name="resourceName"
                    value={appointment.resourceName}
                    onChange={handleChangeAppointment}
                    style={{
                      backgroundColor: "#e9ecef",
                      color: "#6c757d",
                      cursor: "not-allowed",
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            {!scheduling && (
              <>
                <Row className="mb-3">
                  <Form.Group controlId="appointmentServiceId">
                    <Form.Label>Service</Form.Label>
                    {/* Replaces the legacy Form.Select dropdown with the
                        shared ServiceLinePicker search experience. We pass
                        hideCustomCodes so the toggle to custom codes
                        doesn't appear — appointments only ever deal with
                        services. The handler shape mirrors the original
                        onChange so downstream effects keep working. */}
                    <ServiceLinePicker
                      services={services}
                      hideCustomCodes
                      isInvalid={!!errors.serviceId}
                      disabled={servicesLoading || services.length === 0}
                      selectedServiceName={
                        services.find(
                          (s) => s._id === appointment.serviceId
                        )?.servicesName || ""
                      }
                      onClear={() => {
                        // handleServiceChange bails out when no service is
                        // resolved (`if (!selectedService) return;`), so we
                        // can't route a "clear" through it — clear the
                        // appointment state directly instead. The next
                        // onSelect will run handleServiceChange normally
                        // and re-trigger the calculate-end-time flow.
                        setAppointment((prev) => ({
                          ...prev,
                          serviceId: "",
                        }));
                        setServicesDur(undefined);
                        setSelectedServicesId(null);
                        setSelectedServicesDescr("");
                      }}
                      onSelect={(svcId) =>
                        handleServiceChange({
                          target: { name: "serviceId", value: svcId },
                        })
                      }
                    />
                    {errors.serviceId && (
                      <div className="text-danger small mt-1">
                        {errors.serviceId}
                      </div>
                    )}
                    {servicesLoading && !errors.serviceId && (
                      <div className="text-muted small mt-1">
                        Loading services…
                      </div>
                    )}
                    {!servicesLoading &&
                      servicesError &&
                      !errors.serviceId && (
                        <div className="text-danger small mt-1">
                          {servicesError}
                        </div>
                      )}
                    {!servicesLoading &&
                      !servicesError &&
                      services.length === 0 &&
                      !errors.serviceId && (
                        <div className="text-muted small mt-1">
                          No services available
                        </div>
                      )}
                  </Form.Group>
                </Row>
              </>
            )}
            <Row className="mb-3 mt-4">
              {/* Start DateTime */}
              {!recurring && (
                <>
                  <Col>
                    <Form.Group controlId="appointmentStart">
                      <div className="d-flex align-items-center">
                        <Form.Label className="mb-0 me-2">Start</Form.Label>
                        <Form.Control
                          type="datetime-local"
                          name="start"
                          value={appointment.start}
                          onChange={handleChangeAppointment}
                        />
                      </div>
                    </Form.Group>
                  </Col>

                  <Col>
                    <Form.Group controlId="appointmentEnd">
                      <div className="d-flex align-items-center">
                        <Form.Label className="mb-0 me-2">End</Form.Label>
                        <Form.Control
                          type="datetime-local"
                          name="end"
                          value={
                            appointment.end
                              ? formatDateToInputValue(appointment.end)
                              : ""
                          }
                          onChange={handleEndTimeChange}
                          isInvalid={!!errors.end}
                        />
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {errors.end}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
            {recurring && (
              <div className="ms-md-3 border border-secondary rounded p-3 mb-3 position-relative">
                <p className="fw-normal text-primary mb-3 border-bottom">
                  Please complete the recurring appointment details and click on
                  "Check Schedule" to verify availability.
                </p>

                <Row className="mb-2">
                  {/* Weekdays Column */}
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-2">
                      <Form.Label className="mb-1">Recurring On:</Form.Label>
                    </Form.Group>
                    <div className="d-flex flex-wrap">
                      {[
                        "Monday",
                        "Tuesday",
                        "Wednesday",
                        "Thursday",
                        "Friday",
                        "Saturday",
                        "Sunday",
                      ].map((day) => (
                        <div
                          key={day}
                          className="mb-2 me-3"
                          style={{ width: "45%" }}
                        >
                          <Form.Check
                            type="checkbox"
                            label={day}
                            id={`day-${day}`}
                            checked={recurringData.recurringDays?.includes(day)}
                            onChange={() => {
                              const updatedDays =
                                recurringData.recurringDays?.includes(day)
                                  ? recurringData.recurringDays?.filter(
                                      (d) => d !== day
                                    )
                                  : [...recurringData.recurringDays, day];

                              setRecurringData((prevData) => ({
                                ...prevData,
                                recurringDays: updatedDays,
                              }));
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </Col>

                  {/* Form Inputs Column */}
                  <Col xs={12} md={8}>
                    <Row className="mb-3">
                      <Col
                        xs={12}
                        md={3}
                        className="d-flex align-items-center justify-content-md-end mb-2 mb-md-0"
                      >
                        <Form.Label className="mb-0">Start Time:</Form.Label>
                      </Col>
                      <Col xs={12} md={3} className="mb-2 mb-md-0">
                        <Form.Control
                          type="time"
                          name="recurringStartTime"
                          value={recurringData.recurringStartTime}
                          onChange={handleChangeAppointment}
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={3}
                        className="d-flex align-items-center justify-content-md-end mb-2 mb-md-0"
                      >
                        <Form.Label className="mb-0">End Time:</Form.Label>
                      </Col>
                      <Col xs={12} md={3}>
                        <Form.Control
                          type="time"
                          name="recurringEndTime"
                          value={recurringData.recurringEndTime}
                          onChange={handleChangeAppointment}
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col
                        xs={12}
                        md={3}
                        className="d-flex align-items-center justify-content-md-end mb-2 mb-md-0"
                      >
                        <Form.Label className="mb-0">Sessions:</Form.Label>
                      </Col>
                      <Col xs={12} md={3}>
                        <Form.Control
                          type="number"
                          name="numberOfSessions"
                          value={recurringData.numberOfSessions}
                          onChange={handleChangeAppointment}
                          min={1}
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col
                        xs={12}
                        md={3}
                        className="d-flex align-items-center justify-content-md-end mb-2 mb-md-0"
                      >
                        <Form.Label className="mb-0">Start Date:</Form.Label>
                      </Col>
                      <Col xs={12} md={3} className="mb-2 mb-md-0">
                        <Form.Control
                          type="date"
                          name="recurringStartDate"
                          value={recurringData.recurringStartDate}
                          onChange={handleChangeAppointment}
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={3}
                        className="d-flex align-items-center justify-content-md-end mb-2 mb-md-0"
                      >
                        <Form.Label className="mb-0">End Date:</Form.Label>
                      </Col>
                      <Col xs={12} md={3}>
                        <Form.Control
                          type="date"
                          name="recurringEndDate"
                          value={recurringData.recurringEndDate}
                          onChange={handleChangeAppointment}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>

                {/* Button at Bottom Right */}
                <div className="d-flex justify-content-end mt-3">
                  <Button variant="primary" onClick={checkSchedule}>
                    Check Schedule
                  </Button>
                </div>
              </div>
            )}
            <Row className="mb-3">
              <Form.Group as={Col} controlId="appointmentConfirmationEmail">
                <Form.Check
                  type="checkbox"
                  label="Send confirmation email"
                  name="appointmentConfirmationEmail"
                  checked={appointment.appointmentConfirmationEmail}
                  onChange={(e) => {
                    const { name, checked } = e.target;
                    handleChangeAppointment({
                      target: { name, value: checked },
                    });
                  }}
                />
                {intakeEnabled && newPatient && (
                  <>
                    <Form.Check
                      type="checkbox"
                      label="Send Intake form via email"
                      className="mt-1"
                      checked={sendIntakeEmail}
                      disabled={!appointment.patientEmail}
                      title={!appointment.patientEmail ? "Patient email required" : ""}
                      onChange={(e) => setSendIntakeEmail(e.target.checked)}
                    />
                  </>
                )}
              </Form.Group>
            </Row>

            <Row className="mb-3">
              <Form.Group controlId="appointmentNotes">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="notes"
                  value={appointment.notes}
                  onChange={handleChangeAppointment}
                />
              </Form.Group>
            </Row>
            {!scheduling && (
            <Row className="mb-3">
              {/* Practice (existing) */}
              <Form.Group as={Col} md={6} controlId="appointmentPractice">
                <Form.Label>Practice</Form.Label>
                <Form.Select
                  name="practice"
                  value={selectedPracticeId || ""}
                  onChange={(e) => {
                    const practiceId = e.target.value;
                    const selectedPractice = (filteredPractices || []).find(
                      (p) => String(p.practiceNumber) === String(practiceId)
                    );

                    setSelectedPracticeId(practiceId);

                    // Derive locations (backward compatible with legacy address fields)
                    const locs =
                      Array.isArray(selectedPractice?.locations) &&
                      selectedPractice.locations.length > 0
                        ? selectedPractice.locations
                        : [
                            {
                              name:
                                selectedPractice?.primaryLocationName ||
                                "Main Location",
                              address1: selectedPractice?.practiceAddress || "",
                              address2:
                                selectedPractice?.practiceAddress2 || "",
                              city: selectedPractice?.practiceCity || "",
                              province:
                                selectedPractice?.practiceProvince || "",
                              postalCode:
                                selectedPractice?.practicePostalCode || "",
                            },
                          ];

                    setLocationsForPractice(locs);

                    // Always default to the first (primary) location
                    const nextIdx = 0;
                    setSelectedLocationIdx(nextIdx);
                    const firstLoc = locs[nextIdx] || {};

                    // Save practice + chosen location onto the appointment
                    setAppointment((prev) => ({
                      ...prev,
                      practiceId: practiceId,
                      practiceName: selectedPractice?.practiceName || "",
                      location: { ...firstLoc },
                      locationName: firstLoc.name || "Location",
                      locationAddress: [
                        firstLoc.address1,
                        firstLoc.address2,
                        [firstLoc.city, firstLoc.province]
                          .filter(Boolean)
                          .join(", "),
                        firstLoc.postalCode,
                      ]
                        .filter(Boolean)
                        .join(" | "),
                    }));
                  }}
                  isInvalid={!!errors.practiceId}
                >
                  <option value="" disabled>
                    Select a Practice
                  </option>
                  {Array.isArray(filteredPractices) && filteredPractices.length > 0 ? (
                    filteredPractices.map((p) => (
                      <option key={p.practiceNumber} value={p.practiceNumber}>
                        {p.practiceName}
                      </option>
                    ))
                  ) : (
                    <option disabled>No Practices Available</option>
                  )}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.practiceId}
                </Form.Control.Feedback>
              </Form.Group>

              {/* NEW: Location (sits to the right of Practice) */}
              <Form.Group as={Col} md={6} controlId="appointmentLocation">
                <Form.Label>Location</Form.Label>
                <Form.Select
                  name="locationIdx"
                  value={String(selectedLocationIdx ?? 0)}
                  onChange={(e) => {
                    const idx = Number(e.target.value);
                    const loc = (locationsForPractice || [])[idx] || {};
                    setSelectedLocationIdx(idx);

                    // Save the chosen location onto the appointment
                    setAppointment((prev) => ({
                      ...prev,
                      location: { ...loc },
                      locationName: loc.name || "Location",
                      locationAddress: [
                        loc.address1,
                        loc.address2,
                        [loc.city, loc.province].filter(Boolean).join(", "),
                        loc.postalCode,
                      ]
                        .filter(Boolean)
                        .join(" | "),
                    }));
                  }}
                  disabled={
                    !locationsForPractice || locationsForPractice.length === 0
                  }
                >
                  {Array.isArray(locationsForPractice) &&
                  locationsForPractice.length > 0 ? (
                    locationsForPractice.map((loc, idx) => (
                      <option key={`loc-${idx}`} value={String(idx)}>
                        {(loc.name || "Location") +
                          " — " +
                          [loc.address1, loc.city, loc.province]
                            .filter(Boolean)
                            .join(", ")}
                      </option>
                    ))
                  ) : (
                    <option value="0">No Location</option>
                  )}
                </Form.Select>
                <div className="form-text">
                  Defaults to the first location (primary).
                </div>
              </Form.Group>
            </Row>
            )}

            <div className="text-center">
              <Button
                variant="secondary"
                onClick={handleSaveAppointment}
                className="text-white mt-3 w-25 d-flex align-items-center justify-content-center gap-2"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fa-regular fa-floppy-disk"></i>
                    Submit
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <AppToast
        show={toast.show}
        onClose={hideToast}
        variant={toast.variant}
        message={toast.message}
      />

    </>
  );
}
